import {
  CompanyAutosuggest,
  MAXIMUM_CHARACTERS_SEARCH,
  MINIMUM_CHARACTERS_SEARCH,
  useConfig,
  type CompanySuggestion,
} from '@seek/libs-shared';
import { useMelwaysLink } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import { Box, Column, Columns } from 'braid-design-system';
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useNavigate } from 'react-router';
import { useDebounce } from 'use-debounce';

import { CompanyProfilesPage, ROOT_PATH } from '../../../paths';
import type { AppConfig } from '../../../shared/config';
import { useService } from '../../../shared/providers/serviceProvider';

import translations from './.vocab';

export function CompanySearchBar({
  compactMode = false,
  setActiveTab,
}: {
  compactMode?: boolean;
  setActiveTab?: Dispatch<SetStateAction<string | undefined>>;
}) {
  const { t } = useTranslations(translations);
  const { companyProfileService } = useService();
  const config = useConfig<AppConfig>();
  const navigate = useNavigate();
  const { language } = config;
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 200);
  const [suggestions, setSuggestions] = useState<CompanySuggestion[]>([]);
  const [hasHighlightedAutosuggestValue, setHasHighlightedAutosuggestValue] =
    useState(false);

  const localize = useMelwaysLink();

  const DEFAULT_TAB = CompanyProfilesPage.About;

  const searchForCompany = () => {
    if (searchTerm.length) {
      const queryParams = new URLSearchParams({
        term: searchTerm,
      });

      void navigate(`/companies/search?${queryParams.toString()}`);
    }
  };

  const navigateToCompanyProfileUrl = (suggestion: CompanySuggestion) => {
    if (suggestion.slug) {
      if (setActiveTab) {
        setActiveTab(DEFAULT_TAB);
      }
      setSearchTerm('');
      setSuggestions([]);
      setHasHighlightedAutosuggestValue(false);
      void navigate(
        `${localize({
          path: `/${ROOT_PATH}/${encodeURIComponent(suggestion.slug)}`,
          language,
        })}`,
      );
    }
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      const companiesList =
        await companyProfileService.getCompanyProfilesByName({
          name: debouncedSearchTerm,
          zone: config.zone,
          published: true,
          limit: 50,
        });
      setSuggestions(
        companiesList.slice(0, 6).map((company) => ({
          id: company.companyId,
          name: company.name,
          slug: company.slug,
          icon: company.logoUrl,
        })),
      );
    };

    const searchTermLength = debouncedSearchTerm.trim().length;
    if (
      searchTermLength >= MINIMUM_CHARACTERS_SEARCH &&
      searchTermLength <= MAXIMUM_CHARACTERS_SEARCH
    ) {
      void fetchCompanies();
    } else {
      setSuggestions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyProfileService, debouncedSearchTerm]);

  return (
    <Box
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !hasHighlightedAutosuggestValue) {
          setHasHighlightedAutosuggestValue(false);
          searchForCompany();
        }
        if (
          suggestions.length > 0 &&
          (e.key === 'ArrowDown' || e.key === 'ArrowUp')
        ) {
          setHasHighlightedAutosuggestValue(true);
        }
      }}
    >
      <Columns
        space="small"
        align={compactMode ? 'right' : 'left'}
        collapseBelow="tablet"
      >
        <Column width={compactMode ? '1/3' : undefined}>
          <CompanyAutosuggest
            id="company-autosuggest"
            aria-label={t('Search by company name')}
            searchText={searchTerm}
            setSearchText={(text) => {
              setSearchTerm(text);
              if (text.length < MINIMUM_CHARACTERS_SEARCH) {
                setSuggestions([]);
              }
            }}
            placeholder={t('Search by company name')}
            onClickSuggestionItem={navigateToCompanyProfileUrl}
            clearLabel={t('Clear')}
            suggestions={suggestions}
          />
        </Column>
      </Columns>
    </Box>
  );
}
